import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
} from '@nextui-org/react';
import { read, utils, WorkBook, WorkSheet } from 'xlsx';
import {
  requiredFields,
  optionalFields,
  ShipmentRow,
} from '../../utils/shipments-excel-mapping';
import { toast } from 'react-toastify';
import { hasShipmentUploadIdentifier } from 'common/src/utils/shipment-upload-validation';
import { supabase } from 'common/src/supabase';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  excelDateToJSDate,
  splitSizeAndColorFromExcel,
  normalizeColor,
  groupSizeAndColor,
} from '../../utils/functions';
import { SEASONS } from 'common/src/utils/definitions';
import { getExporterSpecies } from 'common/src/api/species';
import { ShipmentBox, Species } from 'common/src/utils/types';
import { parse as parseDate } from 'date-fns';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import {
  suggestMappings,
  FieldMapping,
  MappingSuggestion,
} from '../../utils/column-matcher';
import {
  getExporterMappingProfiles,
  createMappingProfile,
  ColumnMappingProfile,
} from 'common/src/api/column-mappings';
import ColumnMappingModal from './column-mapping-modal';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  getShipmentUploadIdentifierConflicts,
  importLocalShipments,
} from 'common/src/api/shipments';
import {
  getExporterDivisions,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';
import {
  normalizeShipmentReference,
  isValidShipmentReference,
  type ShipmentUploadIdentifierConflict,
} from 'common/src/utils/shipment-upload-validation';
import useTrackingActivationStore, {
  type UploadedShipmentRow,
} from '../../store/useTrackingActivationStore';

type ExtendedShipmentRow = ShipmentRow & {
  reference_number?: string | number;
  container_number?: string | number;
  awb_number?: string | number;
  booking_awb?: string | number;
  season?: string | number;
  species_id?: string | number;
  packing_name?: string | null;
  business_type?: string | null;
  sale_order?: string | null;
  original_incoterm?: string | null;
  fob_price?: number | string | null;
};

interface ExcelUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

/**
 * Extract actual header values from a specific row in the worksheet
 * This avoids the __EMPTY column names that xlsx generates
 */
const extractHeadersFromRow = (
  worksheet: WorkSheet,
  headerRow: number
): string[] => {
  const range = utils.decode_range(worksheet['!ref'] || 'A1');
  const headers: string[] = [];

  // Read the header row (0-indexed)
  const rowIndex = headerRow - 1;

  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = utils.encode_cell({ r: rowIndex, c: col });
    const cell = worksheet[cellAddress];

    if (cell && cell.v) {
      // Cell has a value - use it as the header
      headers.push(String(cell.v).trim());
    } else {
      // Empty cell - skip this column or use a placeholder
      // We'll skip it to avoid __EMPTY columns
      break; // Stop at first empty column
    }
  }

  return headers.filter((h) => h.length > 0);
};

/**
 * Remap data rows to use actual column headers instead of __EMPTY keys
 */
const remapDataWithHeaders = (
  data: Record<string, unknown>[],
  actualHeaders: string[]
): Record<string, unknown>[] => {
  return data.map((row) => {
    const remappedRow: Record<string, unknown> = {};
    const keys = Object.keys(row);

    // Derive an ordered list of keys by their implied column index (from __EMPTY suffix) then by original order.
    const orderedKeys = keys
      .map((key, idx) => {
        const match = key.match(/^__EMPTY(?:_(\d+))?$/);
        const colIndex = match ? Number(match[1] ?? 0) : idx;
        return { key, idx, colIndex };
      })
      .sort((a, b) => a.colIndex - b.colIndex || a.idx - b.idx);

    // Map by column position; if the header already exists in the row, use it directly.
    actualHeaders.forEach((header, colIdx) => {
      if (!header) return;
      if (header in row) {
        remappedRow[header] = row[header];
      } else if (colIdx < orderedKeys.length) {
        remappedRow[header] = row[orderedKeys[colIdx].key] ?? '';
      } else {
        remappedRow[header] = '';
      }
    });

    return remappedRow;
  });
};

/**
 * Convert a worksheet to JSON data rows, using provided headers and
 * skipping the header row itself.
 */
const sheetToDataRows = (
  worksheet: WorkSheet,
  columns: string[],
  headerRowIndex1Based: number
) =>
  utils.sheet_to_json(worksheet, {
    raw: true,
    defval: '',
    header: columns,
    // start reading after the header row (0-based index for range)
    range: Math.max(headerRowIndex1Based, 1),
  }) as Record<string, unknown>[];

const ExcelUploadModal = ({
  isOpen,
  onClose,
  onUploadComplete,
}: ExcelUploadModalProps) => {
  const { userProfile } = useUserStore();
  const { t } = useTranslation('excelUpload');
  const identifierConflictMessages = (
    conflicts: ShipmentUploadIdentifierConflict[]
  ) =>
    conflicts.map((conflict) =>
      t('Referencia conflictiva', {
        reference: conflict.reference_number,
        rows: conflict.row_numbers.join(', ') || '—',
        containers: conflict.container_numbers.join(', ') || '—',
        awbs: conflict.awb_numbers.join(', ') || '—',
      })
    );
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // New state for Excel processing
  const [fileData, setFileData] = useState<ExtendedShipmentRow[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [species, setSpecies] = useState<Species[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<string>('');
  const [selectedPlanillaId, setSelectedPlanillaId] = useState<string>('');
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');
  // Only exporters with several divisions choose one; the rest use the default.
  const isMultiDivision = divisions.length > 1;
  const divisionId = isMultiDivision && selectedDivisionId
    ? Number(selectedDivisionId)
    : undefined;
  const NEW_PLANILLA_VALUE = 'NEW_PLANILLA';
  // Date format is now auto-detected, not selected by user

  const { selectedSeason: storeSelectedSeason } = useFilterStore();
  const { selectedSpecies: storeSelectedSpecies } = useDataStore();

  /* --- Validation state --- */
  const [referencesToCreate, setReferencesToCreate] = useState<string[]>([]);
  const [referencesToReplace, setReferencesToReplace] = useState<string[]>([]);
  const [referencesUntouched, setReferencesUntouched] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [uploadBehavior, setUploadBehavior] = useState<'skip' | 'replace'>(
    'skip'
  ); // New state for upload behavior

  /* --- Smart mapping state --- */
  const [savedProfiles, setSavedProfiles] = useState<ColumnMappingProfile[]>(
    []
  );
  const [rawExcelData, setRawExcelData] = useState<Record<string, unknown>[]>(
    []
  );
  const [exporterColumns, setExporterColumns] = useState<string[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingSuggestions, setMappingSuggestions] = useState<
    MappingSuggestion[]
  >([]);
  const [detectedProfile, setDetectedProfile] =
    useState<ColumnMappingProfile | null>(null);

  /* --- Excel sheet/header selection state --- */
  const [workbookData, setWorkbookData] = useState<WorkBook | null>(null);
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [headerRow, setHeaderRow] = useState<number>(1);
  const [showSheetSelector, setShowSheetSelector] = useState(false);

  // Debug logging: correlate logs with an incremental upload id
  const uploadIdRef = useRef(0);
  const uploadInFlightRef = useRef(false);
  const uploadContextRef = useRef({ onUploadComplete, exporterId: userProfile?.exporter?.id, isOpen });
  uploadContextRef.current = { onUploadComplete, exporterId: userProfile?.exporter?.id, isOpen };
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const logPrefix = () => `[ExcelUploadModal][#${uploadIdRef.current}]`;
  const dbg = (...args: unknown[]) => console.log(logPrefix(), ...args);

  const resetState = () => {
    setFileData([]);
    setFileName('');
    setIsProcessing(false);

    // reset validation related state
    setReferencesToCreate([]);
    setReferencesToReplace([]);
    setReferencesUntouched([]);
    setIsValidated(false);
    setValidationErrors([]);

    // reset mapping state
    setRawExcelData([]);
    setExporterColumns([]);
    setMappingSuggestions([]);
    setDetectedProfile(null);

    // reset sheet selection
    setWorkbookData(null);
    setAvailableSheets([]);
    setSelectedSheet('');
    setHeaderRow(1);
    setShowSheetSelector(false);

    // reset planilla selection
    setSelectedPlanillaId('');
    setSelectedDivisionId('');
  };

  // Ref to the modal body for programmatic scrolling
  const modalBodyRef = useRef<HTMLDivElement | null>(null);

  // Helper: smoothly scroll the modal content to the bottom
  const scrollModalToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = modalBodyRef.current;
      if (!el) return;
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } catch {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, []);

  const handleClose = () => {
    if (uploadInFlightRef.current) return;
    resetState();
    onClose();
  };

  useEffect(() => {
    const fetchSpecies = async () => {
      if (userProfile?.exporter_id) {
        const exporterSpecies = await getExporterSpecies(
          userProfile.exporter_id
        );
        setSpecies(exporterSpecies);
      }
    };
    if (isOpen) {
      fetchSpecies();
    }
  }, [userProfile?.exporter_id, isOpen]);

  useEffect(() => {
    if (!isOpen || !userProfile?.exporter_id) return;
    getExporterDivisions(userProfile.exporter_id)
      .then(setDivisions)
      .catch((error) => console.error('Error loading divisions:', error));
  }, [userProfile?.exporter_id, isOpen]);

  // Load saved mapping profiles
  useEffect(() => {
    const fetchProfiles = async () => {
      if (userProfile?.exporter_id && isOpen) {
        try {
          const profiles = await getExporterMappingProfiles(
            userProfile.exporter_id
          );
          setSavedProfiles(profiles);
        } catch (error) {
          console.error('Error loading mapping profiles:', error);
        }
      }
    };
    fetchProfiles();
  }, [userProfile?.exporter_id, isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (storeSelectedSeason) {
        setSelectedSeason(storeSelectedSeason);
      }
      if (storeSelectedSpecies?.id) {
        setSelectedSpeciesId(String(storeSelectedSpecies.id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-scroll to upload section when a planilla is selected
  useEffect(() => {
    if (
      selectedPlanillaId &&
      selectedPlanillaId !== NEW_PLANILLA_VALUE &&
      uploadSectionRef.current
    ) {
      setTimeout(() => {
        uploadSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [selectedPlanillaId, NEW_PLANILLA_VALUE]);

  // Parse date values (Excel numeric serials or strings) with optional config
  const parseDateValue = (
    value: unknown,
    systemField: string,
    excelColumn: string,
    rowIndex: number
  ): Date => {
    // Excel serial number
    if (typeof value === 'number') {
      const isoLocal = excelDateToJSDate(value);
      const asDate = new Date(isoLocal);
      if (!isNaN(asDate.getTime())) return asDate;
      throw new Error(
        t(
          `Fila {{row}}: No se pudo convertir número Excel a fecha para "{{column}}".`,
          {
            row: rowIndex + 2,
            column: excelColumn,
          }
        )
      );
    }

    // Strings: use configured format if available, else try common formats
    if (typeof value === 'string') {
      const dateOnlyStr = value.split(' ')[0].trim();
      const normalizedValue = dateOnlyStr.replace(/[\.\/]/g, '-');

      // console.log('normalizedValue', normalizedValue);

      const transforms = (detectedProfile?.transforms || {}) as Record<
        string,
        unknown
      >;
      // console.log('transforms', transforms);
      const dateFormatsConfig = (transforms.dateFormats ||
        transforms.date_formats) as Record<string, string> | undefined;
      const configuredFormatRaw =
        dateFormatsConfig?.[systemField] || dateFormatsConfig?.default;
      // Normalize legacy/uppercase tokens for date-fns v2 (D->d, Y->y). Keep M uppercase (month).
      const normalizeTokens = (f: string) =>
        f.replace(/Y/g, 'y').replace(/D/g, 'd');
      const configuredFormat = configuredFormatRaw
        ? normalizeTokens(configuredFormatRaw)
        : undefined;
      console.log('configuredFormat', configuredFormat);
      const tryParseWithFormat = (fmt: string): Date | null => {
        try {
          const d = parseDate(normalizedValue, fmt, new Date());
          if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            if (year < 100) {
              const adjustedYear = year < 50 ? 2000 + year : 1900 + year;
              d.setFullYear(adjustedYear);
            }
            console.log('d', d);
            return d;
          }
        } catch {}
        return null;
      };

      if (configuredFormat) {
        // Manual parse according to configured format to avoid any ambiguity
        const manualParse = (str: string, fmt: string): Date | null => {
          const sep = '-';
          const parts = str.split(sep);
          const tokens = fmt.split(sep);
          if (parts.length !== tokens.length) return null;

          let dNum: number | null = null;
          let mNum: number | null = null;
          let yNum: number | null = null;

          for (let i = 0; i < tokens.length; i++) {
            const t = tokens[i];
            const v = parts[i];
            if (t.includes('d')) dNum = parseInt(v, 10);
            if (t.includes('M')) mNum = parseInt(v, 10);
            if (t.includes('y')) yNum = parseInt(v, 10);
          }

          if (dNum == null || mNum == null || yNum == null) return null;
          if (yNum < 100) yNum = yNum < 50 ? 2000 + yNum : 1900 + yNum;
          // Basic range checks
          if (mNum < 1 || mNum > 12 || dNum < 1 || dNum > 31) return null;

          const candidate = new Date(yNum, mNum - 1, dNum);
          if (
            candidate.getFullYear() === yNum &&
            candidate.getMonth() === mNum - 1 &&
            candidate.getDate() === dNum
          )
            return candidate;
          return null;
        };

        const manual = manualParse(normalizedValue, configuredFormat);
        if (manual) return manual;

        const parsed = tryParseWithFormat(configuredFormat);
        if (parsed) return parsed;
        throw new Error(
          t(
            `Fila {{row}}: Formato de fecha inválido para "{{column}}". Valor "{{value}}" no coincide con "{{format}}".`,
            {
              row: rowIndex + 2,
              column: excelColumn,
              value: value,
              format: configuredFormat,
            }
          )
        );
      }

      const commonFormats = [
        'dd-MM-yyyy',
        'yyyy-MM-dd',
        'MM-dd-yyyy',
        'dd-MM-yy',
        'MM-dd-yy',
        'yy-MM-dd',
      ];
      for (const fmt of commonFormats) {
        const parsed = tryParseWithFormat(fmt);
        if (parsed) return parsed;
      }

      throw new Error(
        t(
          `Fila {{row}}: Formato de fecha inválido para "{{column}}". Valor "{{value}}".`,
          {
            row: rowIndex + 2,
            column: excelColumn,
            value: value,
          }
        )
      );
    }

    throw new Error(
      t(
        `Fila {{row}}: Tipo de dato inválido para fecha en "{{column}}". Se esperaba número o texto.`,
        {
          row: rowIndex + 2,
          column: excelColumn,
        }
      )
    );
  };

  // Process Excel data with a given mapping (NO VALIDATION HERE - just transformation)
  const processExcelWithMapping = useCallback(
    (jsonData: Record<string, unknown>[], mapping: FieldMapping) => {
      try {
        dbg('processExcelWithMapping: start', {
          rows: jsonData?.length,
          mappingKeys: Object.keys(mapping || {}),
        });
        // console.time(logPrefix() + ' transformData');
        const dateFields = [
          'ship_etd',
          'ship_eta',
          'packing_etd',
          'reception_date',
          'process_date',
        ];
        const numberFields = ['quantity', 'box_weight', 'fob_price'];

        // Check if color and caliber are mapped to the same column
        const colorColumn = mapping['color'];
        const caliberColumn = mapping['caliber'];
        const sameColumn =
          colorColumn && caliberColumn && colorColumn === caliberColumn;

        console.log('jsonData', jsonData);

        const transformedData = jsonData.map((row, index) => {
          const transformedRow: Record<
            string,
            string | number | Date | unknown
          > = {};

          // If color and caliber are in the same column, split them first
          let colorValue: string | undefined;
          let caliberValue: string | undefined;

          if (sameColumn && colorColumn) {
            const combinedValue = row[colorColumn];
            if (combinedValue && typeof combinedValue === 'string') {
              const { color, size } = groupSizeAndColor(combinedValue);
              colorValue = color;
              caliberValue = size;
            }
          }

          // Use the mapping to transform excel columns to system fields
          // mapping is now { system_field: excel_column }
          for (const [systemField, excelColumn] of Object.entries(mapping)) {
            let value = row[excelColumn];

            if (value === null || value === undefined || value === '') {
              // Skip optional fields
              continue;
            }

            // Handle color field
            if (systemField === 'color') {
              if (sameColumn && colorValue) {
                // Use the split color value
                transformedRow[systemField] = colorValue;
                continue;
              } else if (typeof value === 'string') {
                // Normalize color from long-form to short-form
                transformedRow[systemField] = normalizeColor(value);
                continue;
              }
            }

            // Handle caliber field
            if (systemField === 'caliber') {
              if (sameColumn && caliberValue) {
                // Use the split caliber value
                transformedRow[systemField] = caliberValue;
                continue;
              }
            }

            if (
              typeof value === 'string' &&
              !dateFields.includes(systemField)
            ) {
              value = value.trim().toUpperCase();
            }

            // Auto-detect and parse date fields (can throw but we'll catch)
            if (dateFields.includes(systemField)) {
              try {
                transformedRow[systemField] = parseDateValue(
                  value,
                  systemField,
                  excelColumn,
                  index
                );
              } catch (err) {
                // Store the raw value, validation will catch it later
                transformedRow[systemField] = value;
              }
            } else if (numberFields.includes(systemField)) {
              // Special handling for box_weight: replace all commas with dots
              if (
                (systemField === 'box_weight' || systemField === 'fob_price') &&
                typeof value === 'string'
              ) {
                const normalized = value.replace(/,/g, '.').trim();
                transformedRow[systemField] = Number(normalized);
              } else {
                transformedRow[systemField] = Number(value);
              }
            } else {
              transformedRow[systemField] = value;
            }
          }
          return transformedRow as unknown as ExtendedShipmentRow;
        }) as ExtendedShipmentRow[];
        console.timeEnd(logPrefix() + ' transformData');
        dbg(
          'processExcelWithMapping: transformedData length',
          transformedData.length
        );
        dbg('transformedData', transformedData);
        setFileData(transformedData);
        setIsProcessing(false); // Stop loading
        dbg('setIsProcessing(false) after transform');
        toast.success(
          t(
            `{{count}} filas procesadas. Presiona "Confirmar Carga" para validar.`,
            {
              count: transformedData.length,
            }
          )
        );
        scrollModalToBottom();
      } catch (error: unknown) {
        setIsProcessing(false); // Stop loading on error
        dbg('processExcelWithMapping: error', error);
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error(t('Error al procesar el archivo'));
        }
        setFileData([]);
      }
    },
    []
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      // Bump upload id and start logging
      uploadIdRef.current += 1;
      dbg('onDrop: start', {
        name: file?.name,
        size: file?.size,
        type: file?.type,
        selectedPlanillaId,
      });

      // Set loading state and file name immediately (synchronously)
      setFileName(file.name);
      setIsProcessing(true);
      dbg('onDrop: setIsProcessing(true) + setFileName');

      // Reset validation state
      setIsValidated(false);
      setReferencesToCreate([]);
      setReferencesToReplace([]);
      setReferencesUntouched([]);
      setValidationErrors([]);
      setFileData([]);

      // Small delay to ensure UI updates before heavy processing
      await new Promise((resolve) => setTimeout(resolve, 50));
      dbg('onDrop: post UI delay');

      const reader = new FileReader();
      reader.onerror = (err) => {
        dbg('FileReader.onerror', err);
        setIsProcessing(false);
        toast.error(t('No se pudo leer el archivo'));
      };
      reader.onload = async (e) => {
        try {
          dbg('FileReader.onload');
          console.time(logPrefix() + ' totalParse');
          // Wait for browser to paint the loading spinner
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(resolve, 0);
              });
            });
          });
          dbg('paint settle done');

          console.time(logPrefix() + ' read->workbook');
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = read(data, { type: 'array' });
          console.timeEnd(logPrefix() + ' read->workbook');
          dbg('workbook parsed', {
            sheetNames: workbook.SheetNames,
            sheetCount: workbook.SheetNames?.length,
          });

          // Store workbook for sheet selection
          setWorkbookData(workbook);

          // Get available sheets
          const sheets = workbook.SheetNames;
          setAvailableSheets(sheets);
          dbg('availableSheets set', sheets);

          // Check if user selected an existing planilla
          if (selectedPlanillaId && selectedPlanillaId !== NEW_PLANILLA_VALUE) {
            const selectedProfile = savedProfiles.find(
              (p) => p.id === Number(selectedPlanillaId)
            );

            if (selectedProfile) {
              // Use planilla's sheet and header row settings
              const sheetToUse = selectedProfile.sheet_name;
              const headerRowToUse = selectedProfile.header_row;

              setSelectedSheet(sheetToUse);
              setHeaderRow(headerRowToUse);
              dbg('profile found', { sheetToUse, headerRowToUse });

              // Auto-process with these settings
              setShowSheetSelector(false);

              // Process the sheet with the profile settings
              try {
                const worksheet = workbook.Sheets[sheetToUse];

                if (!worksheet) {
                  throw new Error(
                    t(`La hoja "{{sheet}}" no existe en el archivo Excel`, {
                      sheet: sheetToUse,
                    })
                  );
                }

                dbg('selected worksheet !ref', worksheet['!ref']);

                // Extract actual column headers from the specified row
                const columns = extractHeadersFromRow(
                  worksheet,
                  headerRowToUse
                );
                dbg('headers extracted', {
                  count: columns.length,
                  sample: columns.slice(0, 10),
                });
                if (columns.length === 0) {
                  setIsProcessing(false);
                  dbg('no headers found with configured headerRow; aborting');
                  throw new Error(
                    t(
                      'No se pudieron extraer encabezados con la configuración de la planilla. Revisa la fila de encabezados en la configuración.'
                    )
                  );
                }

                // Convert sheet to JSON starting after the header row
                console.time(logPrefix() + ' sheet_to_json(profile)');
                const dataRows = sheetToDataRows(
                  worksheet,
                  columns,
                  headerRowToUse
                );
                console.timeEnd(logPrefix() + ' sheet_to_json(profile)');
                dbg('dataRows after parse', dataRows);

                if (dataRows.length === 0) {
                  throw new Error(
                    t('No hay datos después de la fila de encabezados')
                  );
                }

                // Remap data to use actual column headers
                console.time(logPrefix() + ' remapData(profile)');
                const remappedData = remapDataWithHeaders(dataRows, columns);

                console.timeEnd(logPrefix() + ' remapData(profile)');
                dbg('remappedData length', remappedData.length);

                // Store raw data for mapping modal
                setRawExcelData(remappedData);

                // Use the profile's mapping directly
                setDetectedProfile(selectedProfile);
                dbg('processExcelWithMapping with selected profile');
                processExcelWithMapping(
                  remappedData,
                  selectedProfile.mapping_config
                );
              } catch (error: unknown) {
                setIsProcessing(false); // Stop loading on error
                dbg('error processing with profile', error);
                setFileData([]);
                setFileName('');
                if (error instanceof Error) {
                  toast.error(error.message);
                } else {
                  toast.error(t('Error al procesar la hoja'));
                }
              }

              return;
            }
          }

          // For new planilla, go directly to mapping modal with sheet selector inside
          if (selectedPlanillaId === NEW_PLANILLA_VALUE) {
            setSelectedSheet(sheets[0]); // Default to first sheet

            // Process the first sheet with default header row (1)
            const worksheet = workbook.Sheets[sheets[0]];
            dbg('NEW_PLANILLA worksheet !ref', worksheet['!ref']);

            // Extract actual column headers from row 1
            const columns = extractHeadersFromRow(worksheet, 1);
            dbg('NEW_PLANILLA headers', {
              count: columns.length,
              sample: columns.slice(0, 10),
            });

            if (columns.length === 0) {
              // Try to infer header row from the sheet range
              const ref = worksheet['!ref'] as string | undefined;
              let inferredHeaderRow = 1;
              if (ref) {
                const range = utils.decode_range(ref);
                inferredHeaderRow = range.s.r + 1; // 0-indexed to 1-indexed
              }
              dbg('NEW_PLANILLA inferredHeaderRow', inferredHeaderRow);

              const inferredColumns = extractHeadersFromRow(
                worksheet,
                inferredHeaderRow
              );
              dbg('NEW_PLANILLA inferred headers', {
                count: inferredColumns.length,
                sample: inferredColumns.slice(0, 10),
              });

              if (inferredColumns.length > 0) {
                const inferredDataRows = sheetToDataRows(
                  worksheet,
                  inferredColumns,
                  inferredHeaderRow
                );
                const remapped = remapDataWithHeaders(
                  inferredDataRows,
                  inferredColumns
                );
                dbg(
                  'NEW_PLANILLA remapped using inferred header row',
                  remapped.length
                );

                setHeaderRow(inferredHeaderRow);
                setRawExcelData(remapped);
                setExporterColumns(inferredColumns);
                const suggestions = suggestMappings(inferredColumns);
                setMappingSuggestions(suggestions);
                setIsProcessing(false);
                toast.info(
                  t(
                    `Detectamos encabezados en la fila {row}. Revísalos antes de continuar.`,
                    {
                      row: inferredHeaderRow,
                    }
                  )
                );
                setShowMappingModal(true);
                return;
              }

              // Fallback: show selector to ask user
              setIsProcessing(false);
              setShowSheetSelector(true);
              toast.info(
                t('No se encontraron encabezados. Selecciona la fila correcta.')
              );
              return;
            }

            // columns found at row 1, proceed as usual
            const dataRows = sheetToDataRows(worksheet, columns, 1);
            dbg('NEW_PLANILLA dataRows after parse', dataRows.length);

            // Remap data to use actual column headers
            const remappedData = remapDataWithHeaders(dataRows, columns);
            dbg('NEW_PLANILLA remappedData length', remappedData.length);

            setRawExcelData(remappedData);
            setExporterColumns(columns);
            const suggestions = suggestMappings(columns);
            setMappingSuggestions(suggestions);
            setIsProcessing(false); // Stop loading before showing modal
            dbg('NEW_PLANILLA setIsProcessing(false), opening mapping modal');
            setShowMappingModal(true);
            return;
          }

          // For no planilla selected, show selector
          if (sheets.length > 1) {
            setSelectedSheet(sheets[0]); // Default to first sheet
            setIsProcessing(false); // Stop loading
            dbg('multiple sheets detected -> show selector');
            setShowSheetSelector(true);
            toast.info(t('Selecciona la hoja y fila de encabezados'));
          } else {
            // Single sheet - auto-select and show header row selector
            setSelectedSheet(sheets[0]);
            setIsProcessing(false); // Stop loading
            dbg('single sheet detected -> show selector');
            setShowSheetSelector(true);
            toast.info(t('Indica en qué fila están los encabezados'));
          }
        } catch (error: unknown) {
          setIsProcessing(false); // Stop loading on error
          dbg('onDrop error', error);
          if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error(t('Error al procesar el archivo'));
          }
          setFileData([]);
          setFileName('');
        }
        console.timeEnd(logPrefix() + ' totalParse');
      };
      dbg('calling reader.readAsArrayBuffer');
      scrollModalToBottom();
      reader.readAsArrayBuffer(file);
    },
    [
      selectedPlanillaId,
      savedProfiles,
      processExcelWithMapping,
      NEW_PLANILLA_VALUE,
    ]
  );

  // Process selected sheet and header row
  const handleProcessSheet = useCallback(() => {
    if (!workbookData || !selectedSheet) {
      toast.error(t('Por favor selecciona una hoja'));
      return;
    }

    try {
      dbg('handleProcessSheet: begin', { selectedSheet, headerRow });
      const worksheet = workbookData.Sheets[selectedSheet];
      dbg('handleProcessSheet: worksheet !ref', worksheet['!ref']);

      // Extract actual column headers from the specified row
      const columns = extractHeadersFromRow(worksheet, headerRow);
      dbg('handleProcessSheet: headers', {
        count: columns.length,
        sample: columns.slice(0, 10),
      });

      if (columns.length === 0) {
        throw new Error(
          t(
            'No se pudieron extraer las columnas del archivo. Verifica que la fila de encabezados tenga valores.'
          )
        );
      }

      // Convert sheet to JSON starting after the header row
      console.time(logPrefix() + ' sheet_to_json(manual)');
      const dataRows = sheetToDataRows(worksheet, columns, headerRow);
      console.timeEnd(logPrefix() + ' sheet_to_json(manual)');
      dbg('handleProcessSheet: dataRows after parse', dataRows.length);

      if (dataRows.length === 0) {
        throw new Error(t('No hay datos después de la fila de encabezados'));
      }

      // Remap data to use actual column headers
      console.time(logPrefix() + ' remapData(manual)');
      const remappedData = remapDataWithHeaders(dataRows, columns);
      console.timeEnd(logPrefix() + ' remapData(manual)');
      dbg('handleProcessSheet: remappedData length', remappedData.length);

      // Store raw data for mapping modal
      setRawExcelData(remappedData);
      setExporterColumns(columns);

      // Hide sheet selector
      setShowSheetSelector(false);

      // Check if user wants to create a new planilla
      if (selectedPlanillaId === NEW_PLANILLA_VALUE) {
        // Generate AI suggestions and ALWAYS show mapping modal for manual configuration
        const suggestions = suggestMappings(columns);
        setMappingSuggestions(suggestions);
        setShowMappingModal(true);
        return;
      }

      // If a specific planilla was selected, use it
      if (selectedPlanillaId) {
        const selectedProfile = savedProfiles.find(
          (p) => p.id === Number(selectedPlanillaId)
        );

        if (selectedProfile) {
          setDetectedProfile(selectedProfile);
          // Process with this mapping (use remappedData instead of dataRows)
          dbg('handleProcessSheet: processing with selectedProfile');
          processExcelWithMapping(remappedData, selectedProfile.mapping_config);
          return;
        }
      }

      // No planilla selected - show mapping modal
      const suggestions = suggestMappings(columns);
      setMappingSuggestions(suggestions);
      setShowMappingModal(true);
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error(t('Error al procesar la hoja'));
      }
    }
  }, [
    workbookData,
    selectedSheet,
    headerRow,
    savedProfiles,
    selectedPlanillaId,
    processExcelWithMapping,
    NEW_PLANILLA_VALUE,
  ]);

  // Handle mapping confirmation from modal
  const handleMappingConfirm = async (
    mapping: FieldMapping,
    profileName: string | null,
    packingName: string | null,
    dateFormats: Record<string, string>,
    isMultiPacking: boolean
  ) => {
    dbg('handleMappingConfirm: begin', {
      profileName,
      packingName,
      isMultiPacking,
    });
    setShowMappingModal(false);

    // Save profile - now always required
    if (
      !profileName ||
      (!packingName && !isMultiPacking) ||
      !userProfile?.exporter_id
    ) {
      toast.error(t('Error: Falta información de la planilla'));
      return;
    }

    const transforms = { dateFormats, isMultiPacking };

    try {
      dbg('createMappingProfile: call');
      await createMappingProfile(
        userProfile.exporter_id,
        profileName,
        selectedSheet, // sheet_name
        headerRow, // header_row
        mapping,
        packingName || undefined,
        transforms
      );
      dbg('createMappingProfile: success');
      toast.success(
        t(`Planilla "{name}" guardada correctamente`, { name: profileName })
      );

      // Reload profiles
      const profiles = await getExporterMappingProfiles(
        userProfile.exporter_id
      );
      setSavedProfiles(profiles);

      // Store the profile info to use when processing
      setDetectedProfile({
        id: 0,
        exporter_id: userProfile.exporter_id,
        profile_name: profileName,
        packing_name: packingName,
        sheet_name: selectedSheet,
        header_row: headerRow,
        mapping_config: mapping,
        transforms,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(
        t('Error al guardar la planilla, pero continuaremos con la carga')
      );
    }

    // Process the data with the confirmed mapping
    dbg('handleMappingConfirm: processExcelWithMapping');
    processExcelWithMapping(rawExcelData, mapping);
  };

  // Create a ref for programmatic file input trigger
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create a ref for the upload section to scroll to
  const uploadSectionRef = useRef<HTMLDivElement>(null);

  // Handler for "Crear Nueva Planilla" button
  const handleCreateNewPlanilla = () => {
    if (!selectedSeason || !selectedSpeciesId) {
      toast.error(t('Por favor selecciona temporada y especie primero'));
      return;
    }
    setSelectedPlanillaId(NEW_PLANILLA_VALUE);
    // Trigger file input immediately
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: isUploading || !selectedSeason || !selectedSpeciesId || !selectedPlanillaId,
  });

  /* Manual validation function - called when user clicks Confirmar Carga */
  const validateReferences = async () => {
    if (fileData.length === 0 || !selectedSeason || !selectedSpeciesId) return;

    const exporterId = userProfile?.exporter?.id;
    if (!exporterId) {
      toast.error(
        t('No se pudo identificar al exportador para validar las referencias')
      );
      return;
    }

    setIsValidating(true);
    const errors: string[] = [];

    try {
      // 1. FIELD VALIDATIONS - validate each row
      fileData.forEach((row, index: number) => {
        const rowNum = index + 2; // Excel row number (1-indexed + header row)
        // Type assertion for dynamic fields
        const rowData = row as Record<string, unknown>;

        // Required fields validation
        for (const fieldDef of requiredFields) {
          const value = rowData[fieldDef.field];
          if (
            value === null ||
            value === undefined ||
            String(value).trim() === ''
          ) {
            errors.push(
              t(
                `Fila {{row}}: El campo "{{label}}" es obligatorio y está vacío.`,
                {
                  row: rowNum,
                  label: fieldDef.label,
                }
              )
            );
          }
        }
        if (!isValidShipmentReference(rowData.reference_number)) {
          errors.push(t('Fila {{row}}: Cada despacho requiere una referencia válida; no se permite SIN REFERENCIA.', { row: rowNum }));
        }
        if (!hasShipmentUploadIdentifier(rowData)) {
          errors.push(
            t('Fila {{row}}: Debe incluir un número de contenedor o AWB.', {
              row: rowNum,
            })
          );
        }

        // Color validation for species Cerezas (id 2)
        if (selectedSpeciesId === '2') {
          const colorVal = rowData.color;
          if (
            colorVal === null ||
            colorVal === undefined ||
            String(colorVal).trim() === ''
          ) {
            errors.push(
              t(
                `Fila {{row}}: El campo "Color" es obligatorio para Cerezas y está vacío.`,
                { row: rowNum }
              )
            );
          } else {
            const allowedColors = ['L', 'D', 'DD'];
            if (!allowedColors.includes(String(colorVal))) {
              errors.push(
                t(
                  `Fila {{row}}: "Color" inválido para Cerezas. Se encontró "{{value}}". Debe ser 'L', 'D' o 'DD'.`,
                  {
                    row: rowNum,
                    value: rowData.color,
                  }
                )
              );
            }
          }
        }

        // Validate dates are actually Date objects
        if (rowData.ship_etd && !(rowData.ship_etd instanceof Date)) {
          errors.push(
            t(
              `Fila {{row}}: "Fecha ETD" tiene formato inválido. Valor: "{{value}}".`,
              {
                row: rowNum,
                value: rowData.ship_etd,
              }
            )
          );
        }
        if (rowData.ship_eta && !(rowData.ship_eta instanceof Date)) {
          errors.push(
            t(
              `Fila {{row}}: "Fecha ETA" tiene formato inválido. Valor: "{{value}}".`,
              {
                row: rowNum,
                value: rowData.ship_eta,
              }
            )
          );
        }
        if (rowData.packing_etd && !(rowData.packing_etd instanceof Date)) {
          errors.push(
            t(
              `Fila {{row}}: "Fecha Despacho Packing" tiene formato inválido. Valor: "{{value}}".`,
              {
                row: rowNum,
                value: rowData.packing_etd,
              }
            )
          );
        }

        // Validate numbers
        if (rowData.quantity && isNaN(Number(rowData.quantity))) {
          errors.push(
            t(
              `Fila {{row}}: "Cantidad" debe ser un número. Valor: "{{value}}".`,
              {
                row: rowNum,
                value: rowData.quantity,
              }
            )
          );
        }
        if (rowData.box_weight && isNaN(Number(rowData.box_weight))) {
          errors.push(
            t(
              `Fila {{row}}: "Peso Caja" debe ser un número. Valor: "{{value}}".`,
              {
                row: rowNum,
                value: rowData.box_weight,
              }
            )
          );
        }
      });

      if (errors.length === 0) {
        const identifierConflicts = await getShipmentUploadIdentifierConflicts(
          exporterId,
          selectedSeason,
          Number(selectedSpeciesId),
          fileData.map((row, index) => ({
            ...row,
            row_number: headerRow + index + 1,
          })),
          divisionId
        );
        errors.push(...identifierConflictMessages(identifierConflicts));
      }

      // If there are field validation errors, stop here
      if (errors.length > 0) {
        setValidationErrors(errors);
        setIsValidated(false);
        toast.error(
          t(
            `Se encontraron {{count}} errores de validación. Por favor corrige el archivo.`,
            {
              count: errors.length,
            }
          )
        );
        setIsValidating(false);
        return;
      }

      // 2. REFERENCE VALIDATION - check against database using edge function
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-shipment-references?exporter_id=${exporterId}&season=${encodeURIComponent(selectedSeason)}&species_id=${selectedSpeciesId}${divisionId ? `&division_id=${divisionId}` : ''}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Error fetching shipment references'
        );
      }

      const { references } = (await response.json()) as {
        references: string[];
      };
      const referencesInDB = new Set(
        references.map(normalizeShipmentReference)
      );
      const referencesInExcel = new Set(
        fileData.map((row) => normalizeShipmentReference(row.reference_number))
      );

      const toCreate = Array.from(referencesInExcel).filter(
        (ref) => !referencesInDB.has(ref)
      );
      const toReplace = Array.from(referencesInExcel).filter((ref) =>
        referencesInDB.has(ref)
      );
      const untouched = Array.from(referencesInDB).filter(
        (ref) => !referencesInExcel.has(ref)
      );

      setReferencesToCreate(toCreate);
      setReferencesToReplace(toReplace);
      setReferencesUntouched(untouched);
      setValidationErrors([]);
      setIsValidated(true);

      toast.success(
        t('Validación completada. Revisa el resumen y confirma la carga.')
      );
    } catch (err) {
      toast.error(t('Error al validar las referencias'));
      setIsValidated(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmLoad = async () => {
    scrollModalToBottom();
    // This is the first step - validate the data
    await validateReferences();

    scrollModalToBottom();
  };

  const handleUpload = async () => {
    if (uploadInFlightRef.current) return;
    const exporterId = userProfile?.exporter?.id;

    if (!exporterId) {
      toast.error(
        t(
          'No se ha podido identificar al exportador. Por favor, vuelva a iniciar sesión.'
        )
      );
      return;
    }

    if (fileData.length === 0) {
      toast.error(t('Por favor cargue un archivo válido'));
      return;
    }

    uploadInFlightRef.current = true;
    setIsUploading(true);
    const isCurrentUpload = () => mountedRef.current &&
      uploadContextRef.current.isOpen && uploadContextRef.current.exporterId === exporterId;
    try {
      const dataToInsert = fileData.map((row) => {
        const insertRow: Record<string, unknown> = {
          ...row,
          reference_number: normalizeShipmentReference(row.reference_number),
          exporter_id: exporterId,
          created_at: new Date().toISOString(),
          season: selectedSeason,
          species_id: Number(selectedSpeciesId),
          // For multipacking profiles, use the per-row packing_name from the mapped column;
          // otherwise use the profile-level packing_name
          packing_name: detectedProfile?.transforms?.isMultiPacking
            ? row.packing_name || null
            : detectedProfile?.packing_name || row.packing_name || null,
        };

        // Convert Date objects to ISO strings for date fields
        if (insertRow.ship_etd instanceof Date) {
          insertRow.ship_etd = insertRow.ship_etd.toISOString();
        }
        if (insertRow.ship_eta instanceof Date) {
          insertRow.ship_eta = insertRow.ship_eta.toISOString();
        }
        if (insertRow.packing_etd instanceof Date) {
          insertRow.packing_etd = insertRow.packing_etd.toISOString();
        }
        if (insertRow.reception_date instanceof Date) {
          insertRow.reception_date = insertRow.reception_date.toISOString();
        }
        if (insertRow.process_date instanceof Date) {
          insertRow.process_date = insertRow.process_date.toISOString();
        }

        return insertRow;
      });

      const identifierConflicts = await getShipmentUploadIdentifierConflicts(
        exporterId,
        selectedSeason,
        Number(selectedSpeciesId),
        dataToInsert.map((row, index) => ({
          ...row,
          row_number: headerRow + index + 1,
        })),
        divisionId
      );
      if (!isCurrentUpload()) return;
      if (identifierConflicts.length > 0) {
        setValidationErrors(identifierConflictMessages(identifierConflicts));
        setIsValidated(false);
        throw new Error(
          t(
            'Cada Referencia debe identificar un único despacho. Usa Orden de Venta para agrupar despachos relacionados.'
          )
        );
      }

      // The database decides skip/replace against current rows and commits the
      // entire file together. Preview counts may have changed since validation.
      const result = await importLocalShipments({
        exporterId,
        season: selectedSeason,
        speciesId: Number(selectedSpeciesId),
        rows: dataToInsert as Partial<ShipmentBox>[],
        mode: uploadBehavior,
        divisionId,
      });
      if (!isCurrentUpload()) return;
      if (result.created === 0 && result.updated === 0) {
        toast.info(t('No hay referencias nuevas para crear. Todas las referencias ya existen en la base de datos.'));
      } else {
        toast.success(t('Carga completada: {{created}} despachos creados, {{updated}} actualizados y {{skipped}} omitidos.', { ...result }));
      }
      uploadInFlightRef.current = false;
      uploadContextRef.current.onUploadComplete();
      // Tracking starts now instead of at the next scheduled run.
      void useTrackingActivationStore.getState().start({
        exporterId,
        season: selectedSeason,
        speciesId: Number(selectedSpeciesId),
        rows: dataToInsert as UploadedShipmentRow[],
      });
      handleClose();
    } catch (error: unknown) {
      if (!isCurrentUpload()) return;
      if (error && typeof error === 'object' && 'message' in error) {
        toast.error(
          t('Error al cargar el archivo:') + ' ' + (error as Error).message
        );
      } else if (error instanceof Error) {
        toast.error(t('Error al cargar el archivo:') + ' ' + error.message);
      } else {
        toast.error(t('Error al cargar el archivo:'));
      }
    } finally {
      uploadInFlightRef.current = false;
      if (mountedRef.current) setIsUploading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isDismissable={!isUploading}
        isKeyboardDismissDisabled={isUploading}
        size='2xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            {t('Cargar Despachos')}
          </ModalHeader>
          <ModalBody ref={modalBodyRef}>
            <fieldset disabled={isUploading} className='flex min-w-0 flex-col gap-4'>
              {/* General instructions before upload */}
              <div className='p-4 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm text-default-600'>
                <p className='font-semibold mb-5'>
                  {t('Instrucciones Generales')}
                </p>
                {/* <Button
                  className='my-3'
                  color='primary'
                  variant='solid'
                  onPress={downloadTemplate}
                  startContent={<FaFileExcel />}
                >
                  Descargar Plantilla Tipo
                </Button> */}
                <ul className='list-disc ml-5 space-y-1'>
                  <li>
                    {t(
                      'Este módulo te permite cargar fácilmente tus despachos de forma masiva.'
                    )}
                  </li>
                  <li>
                    {t(
                      'Sólo debes subir el archivo Excel de cada una de tus plantas, configurarlo una sóla vez y listo.'
                    )}
                  </li>
                  <li>
                    {t(
                      'Las futuras subidas de despachos sólo necesitarás subir el Excel, seleccionar la planilla y listo.'
                    )}
                  </li>
                </ul>
                <div className='mt-4 rounded-md border border-blue-200 bg-background/70 p-3'>
                  <p className='font-semibold text-foreground'>
                    {t('Qué significa Referencia en CherryMarket')}
                  </p>
                  <p className='mt-1 text-xs text-default-600'>
                    {t(
                      'Referencia identifica un único despacho operativo. Para agrupar despachos relacionados, usa Orden de Venta.'
                    )}
                  </p>
                </div>
              </div>
              <div className='p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200'>
                <p className='font-semibold text-default-700 mb-4'>
                  {t('Campos Solicitados')}
                </p>
                <div className='mt-4 py-3  mb-4 border-b border-green-200 flex items-center gap-4 text-xs'>
                  <div className='flex items-center gap-1.5'>
                    <span className='text-red-500 font-bold'>●</span>
                    <span className='text-default-500'>{t('Obligatorio')}</span>
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <span className='text-default-300 font-bold'>○</span>
                    <span className='text-default-500'>{t('Opcional')}</span>
                  </div>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {/* Shipment Fields */}
                  <div>
                    <h4 className='font-semibold text-sm mb-3 flex items-center gap-2'>
                      {/* <span className='w-1.5 h-1.5 bg-green-600 rounded-full'></span> */}
                      {t('Del Embarque')}
                    </h4>
                    <ul className='space-y-2 text-sm'>
                      {requiredFields
                        .filter((field) => field.shipmentField)
                        .map((field) => (
                          <li className='flex items-start gap-2'>
                            <span className='text-red-500 font-bold text-xs mt-0.5'>
                              ●
                            </span>
                            <div className='flex flex-col gap-1'>
                              <span className='text-default-600'>
                                {field.label}
                              </span>
                              <span className='text-default-400 text-xs'>
                                {field.helperText}
                              </span>
                            </div>
                          </li>
                        ))}
                      <li className='flex items-start gap-2'>
                        <span className='text-red-500 font-bold text-xs mt-0.5'>
                          ●
                        </span>
                        <div className='flex flex-col gap-1'>
                          <span className='text-default-600'>
                            {t('Número de Contenedor o AWB')}
                          </span>
                          <span className='text-default-400 text-xs'>
                            {t(
                              'Debe incluir al menos uno de los dos identificadores'
                            )}
                          </span>
                        </div>
                      </li>
                      {optionalFields
                        .filter((field) => field.shipmentField)
                        .map((field) => (
                          <li className='flex items-start gap-2'>
                            <span className='text-default-300 font-bold text-xs mt-0.5'>
                              ○
                            </span>
                            <div className='flex flex-col gap-1'>
                              <span className='text-default-600'>
                                {field.label}
                              </span>
                              <span className='text-default-400 text-xs'>
                                {field.helperText}
                              </span>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>

                  {/* Box Fields */}
                  <div>
                    <h4 className='font-semibold text-sm mb-3 flex items-center gap-2'>
                      {/* <span className='w-1.5 h-1.5 bg-green-600 rounded-full'></span> */}
                      {t('De la Carga')}
                    </h4>
                    <ul className='space-y-2 text-sm'>
                      {requiredFields
                        .filter((field) => !field.shipmentField)
                        .map((field) => (
                          <li className='flex items-start gap-2'>
                            <span className='text-red-500 font-bold text-xs mt-0.5'>
                              ●
                            </span>
                            <div className='flex flex-col gap-1'>
                              <span className='text-default-600'>
                                {field.label}
                              </span>
                              <span className='text-default-400 text-xs'>
                                {field.helperText}
                              </span>
                            </div>
                          </li>
                        ))}
                      {optionalFields
                        .filter((field) => !field.shipmentField)
                        .map((field) => (
                          <li className='flex items-start gap-2'>
                            <span className='text-default-300 font-bold text-xs mt-0.5'>
                              ○
                            </span>
                            <div className='flex flex-col gap-1'>
                              <span className='text-default-600'>
                                {field.label}
                              </span>
                              <span className='text-default-400 text-xs'>
                                {field.helperText}
                              </span>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <Select
                  label={t('Temporada')}
                  placeholder={t('Seleccione una temporada')}
                  selectedKeys={selectedSeason ? [selectedSeason] : []}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  isRequired
                >
                  {Object.values(SEASONS).map((season) => (
                    <SelectItem key={season.name} value={season.name}>
                      {season.name}
                    </SelectItem>
                  ))}
                </Select>
                <Select
                  label={t('Especie')}
                  placeholder={t('Seleccione una especie')}
                  selectedKeys={selectedSpeciesId ? [selectedSpeciesId] : []}
                  onChange={(e) => setSelectedSpeciesId(e.target.value)}
                  isDisabled={species.length === 0}
                  isRequired
                >
                  {species.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </Select>
                {isMultiDivision && (
                  <Select
                    className='col-span-2'
                    label={t('División')}
                    placeholder={t('Seleccione una división')}
                    selectedKeys={selectedDivisionId ? [selectedDivisionId] : []}
                    onChange={(e) => {
                      setSelectedDivisionId(e.target.value);
                      // References are checked per division; re-validate.
                      setIsValidated(false);
                    }}
                    isRequired
                  >
                    {divisions.map((division) => (
                      <SelectItem key={division.id} value={division.id}>
                        {division.name}
                      </SelectItem>
                    ))}
                  </Select>
                )}
              </div>

              {/* Planilla selection - separate from season/species */}
              <div className='flex flex-col gap-3'>
                <label className='text-sm font-medium text-default-600'>
                  {t('Planilla')} <span className='text-red-500'>*</span>
                </label>
                {savedProfiles.length > 0 ? (
                  <div className='flex gap-3'>
                    <Select
                      placeholder={t('Selecciona una planilla guardada')}
                      selectedKeys={
                        selectedPlanillaId &&
                        selectedPlanillaId !== NEW_PLANILLA_VALUE
                          ? [selectedPlanillaId]
                          : []
                      }
                      onChange={(e) => setSelectedPlanillaId(e.target.value)}
                      className='flex-1'
                      isDisabled={!selectedSeason || !selectedSpeciesId}
                    >
                      {savedProfiles.map((profile) => (
                        <SelectItem
                          key={profile.id}
                          value={profile.id.toString()}
                        >
                          {profile.profile_name}
                        </SelectItem>
                      ))}
                    </Select>
                    <Button
                      variant='flat'
                      onPress={handleCreateNewPlanilla}
                      className='min-w-fit'
                      isDisabled={
                        !selectedSeason || !selectedSpeciesId || isProcessing
                      }
                    >
                      {isProcessing ? <Spinner size='sm' /> : '+'}{' '}
                      {isProcessing
                        ? t('Leyendo...')
                        : t('Configurar Nueva Planilla')}
                    </Button>
                  </div>
                ) : (
                  <Button
                    color='primary'
                    onPress={handleCreateNewPlanilla}
                    className='w-full'
                    isDisabled={!selectedSeason || !selectedSpeciesId}
                  >
                    + {t('Crear Nueva Planilla')}
                  </Button>
                )}
                {selectedPlanillaId === NEW_PLANILLA_VALUE && fileName && (
                  <p className='text-xs text-blue-600'>
                    ✓ {t('Archivo cargado:')} {fileName}
                  </p>
                )}
              </div>

              {/* Hidden file input for NEW_PLANILLA mode */}
              <input
                ref={fileInputRef}
                type='file'
                accept='.xlsx,.xls'
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    // Set processing state immediately and give React time to render
                    setFileName(files[0].name);
                    setIsProcessing(true);

                    // Small delay to ensure UI updates before calling onDrop
                    await new Promise((resolve) => setTimeout(resolve, 100));

                    onDrop([files[0]]);
                  }
                  // Reset the input value so the same file can be selected again
                  e.target.value = '';
                }}
              />

              {/* Processing Loader */}
              {isProcessing && (
                <div className='mt-4 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-2 border-indigo-300 shadow-lg'>
                  <div className='flex items-center gap-4'>
                    <Spinner size='lg' color='primary' />
                    <div>
                      <p className='font-semibold text-lg text-indigo-900'>
                        {t('Leyendo archivo Excel...')}
                      </p>
                      <p className='text-sm text-indigo-700 mt-1'>
                        {t(
                          'Por favor espera mientras procesamos tu archivo. Esto puede tomar unos momentos.'
                        )}
                      </p>
                    </div>
                  </div>
                  {fileName && (
                    <p className='text-xs text-indigo-600 mt-3 ml-14'>
                      📄 {fileName}
                    </p>
                  )}
                </div>
              )}

              {/* Only show drag-and-drop for existing planilla selection */}
              {selectedPlanillaId &&
                selectedPlanillaId !== NEW_PLANILLA_VALUE && (
                  <div ref={uploadSectionRef}>
                    <div className='flex justify-between items-center mt-4'>
                      <p className='text-sm text-default-500'>
                        {t(
                          'Sube un archivo excel para cargar despachos masivamente.'
                        )}
                      </p>
                    </div>

                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                      ${isDragActive ? 'border-primary bg-primary/10' : 'border-divider'}
                      ${!selectedSeason || !selectedSpeciesId || !selectedPlanillaId ? 'bg-default-100 cursor-not-allowed' : ''}`}
                    >
                      <input {...getInputProps()} />
                      {fileName ? (
                        <p className='text-success'>
                          {t('Archivo seleccionado:')} {fileName}
                        </p>
                      ) : (
                        <p>
                          {t(
                            'Arrastre un archivo Excel o haga click para seleccionar'
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

              {/* Sheet and Header Row Selector */}
              {showSheetSelector && !isProcessing && (
                <div className='p-4 bg-orange-50 rounded-lg border border-orange-200'>
                  <h3 className='text-base font-semibold text-orange-900 mb-3'>
                    Configurar archivo Excel
                  </h3>

                  <div className='grid grid-cols-2 gap-4'>
                    {/* Sheet Selector */}
                    {availableSheets.length > 1 && (
                      <Select
                        label='Seleccionar Hoja'
                        placeholder='Seleccione una hoja'
                        selectedKeys={selectedSheet ? [selectedSheet] : []}
                        onChange={(e) => setSelectedSheet(e.target.value)}
                        size='sm'
                      >
                        {availableSheets.map((sheet) => (
                          <SelectItem key={sheet} value={sheet}>
                            {sheet}
                          </SelectItem>
                        ))}
                      </Select>
                    )}

                    {/* Header Row Selector */}
                    <Input
                      type='number'
                      label='Fila de Encabezados'
                      placeholder='Ej: 1'
                      value={String(headerRow)}
                      onValueChange={(val) => setHeaderRow(Number(val) || 1)}
                      min={1}
                      max={100}
                      size='sm'
                      description='¿En qué fila están los nombres de columnas?'
                    />
                  </div>

                  <Button
                    color='primary'
                    className='mt-4 w-full'
                    onPress={handleProcessSheet}
                  >
                    Continuar con esta configuración
                  </Button>
                </div>
              )}

              {fileData.length > 0 &&
                !isValidated &&
                !isValidating &&
                !isProcessing &&
                validationErrors.length === 0 && (
                  <div className='mt-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200'>
                    <div className='flex items-center gap-2'>
                      <div className='w-2 h-2 rounded-full bg-blue-50 dark:bg-blue-9500' />
                      <span className='font-medium text-blue-700'>
                        {fileData.length} filas procesadas
                      </span>
                    </div>
                    <p className='text-sm text-blue-600 mt-2'>
                      Presiona "Confirmar Carga" para validar las referencias
                      contra la base de datos.
                    </p>
                  </div>
                )}

              {/* Validation Errors Section */}
              {validationErrors.length > 0 &&
                !isValidating &&
                !isProcessing && (
                  <div className='mt-4 p-4 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200'>
                    <h3 className='text-base font-semibold text-red-900 mb-3 flex items-center gap-2'>
                      <span className='text-2xl'>⚠️</span>
                      Errores de Validación
                    </h3>
                    <p className='text-sm text-red-700 mb-3'>
                      Se encontraron {validationErrors.length} errores en el
                      archivo. Por favor corrige estos errores y vuelve a cargar
                      el archivo.
                    </p>
                    <div className='max-h-60 overflow-y-auto space-y-2'>
                      {validationErrors.map((error, index) => (
                        <div
                          key={index}
                          className='bg-background p-2 rounded border border-red-200'
                        >
                          <p className='text-sm text-red-800'>{error}</p>
                        </div>
                      ))}
                    </div>
                    <Button
                      variant='flat'
                      className='mt-4'
                      onPress={() => {
                        // Reset everything to allow re-upload
                        setFileData([]);
                        setFileName('');
                        setValidationErrors([]);
                        setIsValidated(false);
                      }}
                    >
                      Cargar Nuevo Archivo
                    </Button>
                  </div>
                )}

              {isValidating && (
                <div className='flex items-center text-sm text-default-500 mt-2 gap-2'>
                  <Spinner size='sm' />
                  <span>Validando referencias...</span>
                </div>
              )}

              {isValidated && !isValidating && (
                <div className='mt-4 p-4 bg-default-100 rounded-lg border border-divider'>
                  <h3 className='text-base font-semibold text-foreground mb-3'>
                    Resumen de referencias
                  </h3>

                  {/* Upload behavior selector - only show if there are references to replace */}
                  {referencesToReplace.length > 0 && (
                    <div className='mb-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200'>
                      <label className='text-sm font-medium text-default-600 mb-2 block'>
                        ¿Qué hacer con las referencias existentes?
                      </label>
                      <Select
                        size='sm'
                        selectedKeys={[uploadBehavior]}
                        onSelectionChange={(keys) =>
                          setUploadBehavior(
                            Array.from(keys)[0] as 'skip' | 'replace'
                          )
                        }
                        className='max-w-md'
                      >
                        <SelectItem key='skip' value='skip'>
                          Omitir (no modificar existentes)
                        </SelectItem>
                        <SelectItem key='replace' value='replace'>
                          Actualizar (conservar ID)
                        </SelectItem>
                      </Select>
                      <p className='text-xs text-default-500 mt-2'>
                        {uploadBehavior === 'skip'
                          ? '✓ Las referencias existentes no serán modificadas. Solo se crearán las nuevas.'
                          : 'Las referencias existentes se actualizarán conservando su ID y los precios con coincidencia única.'}
                      </p>
                    </div>
                  )}

                  <div className='space-y-4'>
                    {/* Referencias a crear */}
                    <div className='bg-green-50 dark:bg-green-950 p-3 rounded-md border border-green-100'>
                      <div className='flex items-center gap-2 mb-2'>
                        <div className='w-2 h-2 rounded-full bg-green-50 dark:bg-green-9500' />
                        <span className='font-medium text-green-700'>
                          {referencesToCreate.length} referencias serán creadas
                        </span>
                      </div>
                      {referencesToCreate.length > 0 && (
                        <div className='ml-4 space-y-1'>
                          {referencesToCreate.map((ref) => (
                            <p key={ref} className='text-sm text-green-600'>
                              {ref}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Referencias a reemplazar o omitir */}
                    {referencesToReplace.length > 0 && (
                      <div
                        className={
                          uploadBehavior === 'skip'
                            ? 'bg-blue-50 dark:bg-blue-950 p-3 rounded-md border border-blue-100'
                            : 'bg-yellow-50 p-3 rounded-md border border-yellow-100'
                        }
                      >
                        <div className='flex items-center gap-2 mb-2'>
                          <div
                            className={`w-2 h-2 rounded-full ${uploadBehavior === 'skip' ? 'bg-blue-50 dark:bg-blue-9500' : 'bg-yellow-500'}`}
                          />
                          <span
                            className={`font-medium ${uploadBehavior === 'skip' ? 'text-blue-700' : 'text-yellow-700'}`}
                          >
                            {referencesToReplace.length} referencias serán{' '}
                            {uploadBehavior === 'skip'
                              ? 'omitidas'
                              : 'actualizadas'}
                          </span>
                        </div>
                        {referencesToReplace.length > 0 && (
                          <div className='ml-4 space-y-1'>
                            {referencesToReplace.map((ref) => (
                              <p
                                key={ref}
                                className={`text-sm ${uploadBehavior === 'skip' ? 'text-blue-600' : 'text-yellow-600'}`}
                              >
                                {ref}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Referencias sin cambios */}
                    <div className='bg-default-100 p-3 rounded-md border border-divider'>
                      <div className='flex items-center gap-2 mb-2'>
                        <div className='w-2 h-2 rounded-full bg-default-400' />
                        <span className='font-medium text-default-600'>
                          {referencesUntouched.length} referencias no serán
                          tocadas
                        </span>
                      </div>
                      {referencesUntouched.length > 0 && (
                        <div className='ml-4 space-y-1'>
                          {referencesUntouched.map((ref) => (
                            <p key={ref} className='text-sm text-default-500'>
                              {ref}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </fieldset>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={handleClose}>
              Cancelar
            </Button>
            {!isValidated && validationErrors.length === 0 ? (
              <Button
                color='primary'
                isLoading={isValidating}
                onPress={handleConfirmLoad}
                isDisabled={
                  fileData.length === 0 ||
                  !selectedSeason ||
                  !selectedSpeciesId ||
                  (isMultiDivision && !selectedDivisionId) ||
                  isValidating
                }
              >
                Confirmar Carga
              </Button>
            ) : isValidated ? (
              <Button
                color='primary'
                isLoading={isUploading}
                onPress={handleUpload}
                isDisabled={isUploading}
              >
                Cargar Datos
              </Button>
            ) : null}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Column Mapping Modal */}
      <ColumnMappingModal
        isOpen={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        exporterColumns={exporterColumns}
        sampleData={rawExcelData.slice(0, 3)} // Show first 3 rows as sample
        initialSuggestions={mappingSuggestions}
        onConfirm={handleMappingConfirm}
        availableSheets={availableSheets}
        selectedSheet={selectedSheet}
        onSheetChange={(sheet) => {
          setSelectedSheet(sheet);
          // Reprocess the sheet when it changes
          if (workbookData) {
            const worksheet = workbookData.Sheets[sheet];

            // Extract actual column headers from the specified row
            const columns = extractHeadersFromRow(worksheet, headerRow);

            const dataRows = sheetToDataRows(worksheet, columns, headerRow);

            // Remap data to use actual column headers
            const remappedData = remapDataWithHeaders(dataRows, columns);

            setRawExcelData(remappedData);
            setExporterColumns(columns);
            const suggestions = suggestMappings(columns);
            setMappingSuggestions(suggestions);
          }
        }}
        headerRow={headerRow}
        onHeaderRowChange={(row) => {
          setHeaderRow(row);
          // Reprocess when header row changes
          if (workbookData && selectedSheet) {
            const worksheet = workbookData.Sheets[selectedSheet];

            // Extract actual column headers from the specified row
            const columns = extractHeadersFromRow(worksheet, row);

            const dataRows = sheetToDataRows(worksheet, columns, row);

            // Remap data to use actual column headers
            const remappedData = remapDataWithHeaders(dataRows, columns);

            setRawExcelData(remappedData);
            setExporterColumns(columns);
            const suggestions = suggestMappings(columns);
            setMappingSuggestions(suggestions);
          }
        }}
        showSheetSelector={selectedPlanillaId === NEW_PLANILLA_VALUE}
        initialDateFormats={undefined}
      />
    </>
  );
};

export default ExcelUploadModal;
